
import type { BrowseConfiguration } from '@empathybroker/tsclient-play/dist/models/browse-configuration'
import type { Facet } from '@empathybroker/tsclient-play/dist/models/facet'
import type { PropType } from 'vue'
import type { BrowseFacetServiceListParams } from '../../services/interfaces'
import { computed, defineComponent, ref, watch } from 'vue'
import { useBrowseFacet } from '../../composables/use-browse-facet'
import { FacetSortTypeEnum } from '../../enums'
import { capitalizeSentence } from '../../filters/capitalize-sentence.filter'
import { EpbButton } from '../shared/epb-button'
import { EpbSelect } from '../shared/epb-select'
import { EpbTextbox } from '../shared/epb-textbox'

import FacetsConfigurationList from './facets-configuration-list.component.vue'

export interface UiSelectOption {
  value: string
  label: string
}

export default defineComponent({
  name: 'FacetsConfiguration',
  components: {
    EpbButton,
    EpbSelect,
    EpbTextbox,
    FacetsConfigurationList,
  },
  props: {
    item: {
      type: Object as PropType<BrowseConfiguration>,
      required: true,
    },
    readOnly: Boolean,
    messages: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { facets, loading: loadingFacets, list: listFacets } = useBrowseFacet()

    const selectedIdOption = ref<UiSelectOption | ''>('')

    const idSelectOptions = computed<Array<UiSelectOption>>(() => {
      return facets.value.reduce<Array<UiSelectOption>>((result, facet) => {
        if (!props.item.facets.elements.some(facetElement => facetElement.id === facet.id)) {
          result.push({
            value: facet.id,
            label: capitalizeSentence(facet.label || facet.id),
          })
        }
        return result
      }, [])
    })

    const listFacetsParams = computed<BrowseFacetServiceListParams>(() => {
      const field = props.item.context.attributeName
      const value = props.item.context.attributeValues[0]?.id
      const pivots = Object.entries(props.item.context.pivots).reduce<
        BrowseFacetServiceListParams['pivots']
      >(
        (result, [pivotName, [pivotValue]]) => [
          ...result,
          { name: pivotName, values: [pivotValue] },
        ],
        [],
      )
      return {
        field,
        value,
        pivots,
      }
    })

    watch(
      listFacetsParams,
      async value => {
        try {
          const result = await listFacets(value)
          const selectedId = (selectedIdOption.value as UiSelectOption).value
          if (selectedId && !result.some(facet => facet.id === selectedId)) {
            selectedIdOption.value = ''
          }
          props.item.facets.elements = props.item.facets.elements.filter(facetElement =>
            result.some(facet => facet.id === facetElement.id),
          )
        } catch (error) {}
      },
      { deep: true, immediate: true },
    )

    return {
      selectedIdOption,
      loadingFacets,
      idSelectOptions,
    }
  },
  data() {
    return {
      formValues: {
        prettyName: '',
        sortType: '',
      },
      capitalizeSentence,
    }
  },
  computed: {
    sortTypeOptions(): any[] {
      const options = Object.values(FacetSortTypeEnum).map((sortType: FacetSortTypeEnum) => ({
        value: sortType,
        label: this.messages.sortType.values[sortType],
      }))
      options.unshift({
        value: undefined,
        label: this.messages.sortType.values.default,
      })
      return options
    },
    addFacetDisabled(): boolean {
      return !this.selectedIdOption
    },
  },
  methods: {
    addFacet(): void {
      this.item.facets.elements.push({
        id: this.selectedIdOption.value,
        prettyName: this.formValues.prettyName,
        sortType: this.formValues.sortType.value,
        values: [],
      } as Facet)
      this.selectedIdOption = ''
      this.formValues = {
        prettyName: '',
        sortType: '',
      }
    },
  },
})
